/**
 * Confirm sending the customer a new booking link (BON-472).
 *
 * WHY A CONFIRMATION AT ALL: only a digest of the credential is stored, so there
 * is no original link to re-send — a resend necessarily mints a new one and kills
 * the old (ADR-0023). A misclick on a row whose link is working perfectly well
 * destroys it. The CSR has to know that before they click, not after.
 *
 * THE WARNING IS THE REASON THIS DIALOG EXISTS, so it is wired up as the
 * dialog's description. Labelled alone, a screen reader announces "Send a new
 * booking link, dialog" and stops — and the one person who most needs telling
 * that this cuts the customer off is the one who never hears it.
 *
 * NOTHING DISMISSES IT WHILE THE REQUEST IS OUT. Cancel, Escape and the backdrop
 * cannot call a resend back; all three would do is hide the answer from a CSR
 * who now believes nothing was sent.
 */
import { useRef, useState } from 'react';

import { resendReservationLink, type ReservationRow } from '../../api/reservations';
import { useDialogKeyboard } from '../../hooks/useDialogKeyboard';
import { DialogFooterActions } from '../../shared/DialogFooterActions';

import { RESEND_WARNING_ID, ResendReservationWarning } from './ResendReservationWarning';
import {
  NO_EMAIL,
  requestFailureOutcome,
  resendOutcome,
  type RotationOutcome,
} from './reservationRotationOutcome';

const TITLE_ID = 'resend-reservation-title';

export function ResendReservationDialog({
  reservation,
  onClose,
  onDone,
}: {
  reservation: ReservationRow;
  onClose: () => void;
  /** Fired for every finished attempt, refusals included — the caller owns the
   *  toast, the error dialog and the list refresh. */
  onDone: (outcome: RotationOutcome) => void;
}) {
  const [sending, setSending] = useState(false);
  // Focus lands on Cancel, not Send: this dialog exists to stop an accidental
  // rotation, and a focused confirm button turns a stray Enter into one.
  const cancelRef = useRef<HTMLButtonElement>(null);
  useDialogKeyboard(onClose, cancelRef, sending);

  const send = async () => {
    if (sending) return;
    setSending(true);
    try {
      onDone(resendOutcome(await resendReservationLink(reservation.id)));
    } catch (err) {
      onDone(requestFailureOutcome('resend', err));
    }
  };

  const dismiss = () => {
    if (!sending) onClose();
  };

  return (
    <div
      role="presentation"
      onClick={dismiss}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        aria-describedby={RESEND_WARNING_ID}
        className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-[#1a1f1c]"
      >
        <div className="border-b border-stone-200 px-6 py-4 dark:border-stone-700">
          <h2 id={TITLE_ID} className="text-lg font-bold text-bark-900 dark:text-stone-100">
            Send a new booking link
          </h2>
          <p className="mt-0.5 text-xs text-stone-500 dark:text-stone-400">
            {reservation.reference} · {reservation.customerEmail ?? NO_EMAIL} ·{' '}
            {reservation.workshopName}
          </p>
        </div>

        <div className="px-6 py-5">
          <ResendReservationWarning reservation={reservation} />
        </div>

        <DialogFooterActions
          onCancel={dismiss}
          cancelRef={cancelRef}
          confirmLabel="Send new link"
          confirmBusyLabel="Sending…"
          confirmType="button"
          onConfirm={send}
          busy={sending}
        />
      </div>
    </div>
  );
}
